import { useState, useEffect } from 'react';
import DocumentWallet from './DocumentWallet';
import api from '../utils/api';

const EMPTY = {
  client_wo_number: '', engagement_type: 'contract_placement', client_id: '', vendor_id: '',
  contractor_id: '', start_date: '', end_date: '', sell_rate: '', buy_rate: '', fte_fee_amount: '', offer_number: '', employee_id: '', notes: '',
  person_id: '', commission_type: '', commission_value: '', billing_frequency: '', scope_description: '',
};

// Business Consulting WOs default to year end, same as the CWK renewals.
const CONSULTING_DEFAULT_END = '2026-12-31';
const BILLING_FREQUENCIES = [
  ['weekly', 'Weekly'], ['biweekly', 'Biweekly'], ['monthly', 'Monthly'], ['bimonthly', 'Bimonthly'], ['ad_hoc', 'Ad hoc'],
];
const TYPE_LABELS = {
  contract_placement: 'Contract Placement', recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement', business_consulting: 'Business Consulting',
};

const ENGAGEMENT_INFO = {
  contract_placement: 'TMV sits between the client and vendor. Requires a vendor — the contractor is paid through them.',
  recurring_referral: 'TMV brokers an introduction and earns a referral fee. No vendor involved — TMV never pays anyone in this model.',
  fte_placement: 'A one-time placement fee, paid once after the contractor qualifies as a full-time hire. No recurring relationship.',
  business_consulting: 'TMV bills the client directly for consulting work. No contractor, no vendor, no rates - each invoice carries its own period and line items. A client PO is optional.',
};

export default function WorkOrderEditDrawer({ workOrder, onClose, onSaved }) {
  const [form, setForm] = useState(() => workOrder ? { ...EMPTY, ...workOrder } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(workOrder?.id);

  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data));
    api.get('/vendors').then(r => setVendors(r.data));
    api.get('/contractors').then(r => setContractors(r.data));
    api.get('/people').then(r => setPeople(r.data));
  }, []);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function setEngagementType(type) {
    setForm(f => ({
      ...f,
      engagement_type: type,
      // Clear fields that don't apply to the new type
      vendor_id: type === 'contract_placement' ? f.vendor_id : '',
      buy_rate: type === 'contract_placement' ? f.buy_rate : '',
      contractor_id: type === 'business_consulting' ? '' : f.contractor_id,
      sell_rate: type === 'business_consulting' ? '' : f.sell_rate,
      commission_type: type === 'business_consulting' ? '' : f.commission_type,
      end_date: type === 'business_consulting' && !f.end_date ? CONSULTING_DEFAULT_END : f.end_date,
    }));
  }

  const selectedContractor = contractors.find(c => c.id === Number(form.contractor_id));
  const isConsulting = form.engagement_type === 'business_consulting';

  function selectContractor(contractorId) {
    const c = contractors.find(x => x.id === Number(contractorId));
    setForm(f => ({
      ...f,
      contractor_id: contractorId,
      // A vendor never applies to W2/1099 — clear it immediately if the
      // newly selected contractor isn't corp-to-corp, so a stale vendor
      // choice from a previous selection can't slip through.
      vendor_id: c && c.contractor_subtype !== 'corp_to_corp' ? '' : f.vendor_id,
    }));
  }

  async function handleSave() {
    if (!form.client_wo_number || !form.client_id || (!form.contractor_id && !isConsulting)) {
      setError(isConsulting ? 'Work Order number and client are required' : 'Work Order number, client, and contractor are required');
      return;
    }
    if (!isEdit && !form.person_id) {
      setError('An owner must be assigned — every Work Order needs a Person responsible for it, even if no commission applies');
      return;
    }
    if (form.engagement_type === 'contract_placement' && selectedContractor?.contractor_subtype === 'corp_to_corp' && !form.vendor_id) {
      setError('This contractor is Corp-to-Corp — a vendor is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        client_id: Number(form.client_id),
        contractor_id: isConsulting ? null : Number(form.contractor_id),
        vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
        sell_rate: form.sell_rate ? Number(form.sell_rate) : null,
        buy_rate: form.buy_rate ? Number(form.buy_rate) : null,
        fte_fee_amount: form.fte_fee_amount ? Number(form.fte_fee_amount) : null,
        person_id: form.person_id ? Number(form.person_id) : null,
        commission_value: form.commission_type && form.commission_value ? Number(form.commission_value) : null,
        billing_frequency: isConsulting ? (form.billing_frequency || null) : undefined,
        scope_description: isConsulting ? (form.scope_description || null) : undefined,
      };
      if (isEdit) {
        await api.put(`/work-orders/${workOrder.id}`, payload);
      } else {
        await api.post('/work-orders', payload);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save Work Order');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="edit-drawer">
        <div className="drawer-header">
          <h3 className="drawer-title">{isEdit ? 'Edit Work Order' : 'New Work Order'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 18 }}>
              {error}
            </div>
          )}

          <div className="form-field span-2" style={{ marginBottom: 18 }}>
            <label>Engagement type</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.keys(ENGAGEMENT_INFO).map(type => (
                <button
                  key={type}
                  className={form.engagement_type === type ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ flex: 1 }}
                  onClick={() => setEngagementType(type)}
                  disabled={isEdit}
                >
                  {TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <p className="form-hint" style={{ marginTop: 8 }}>{ENGAGEMENT_INFO[form.engagement_type]}</p>
            {isEdit && <p className="form-hint">Engagement type can't be changed after creation.</p>}
          </div>

          <div className="form-grid">
            <div className="form-field span-2">
              <label>Client Work Order number</label>
              <input value={form.client_wo_number} onChange={e => set('client_wo_number', e.target.value)} placeholder={isConsulting ? 'Engagement / SOW reference, e.g. BC-VITS-2026' : "The client's own SOW # / CWK #"} autoFocus />
              {form.engagement_type === 'contract_placement' && form.client_wo_number && (
                <p className="form-hint">Outgoing WO to the vendor will be auto-numbered: TMV-{form.client_wo_number}</p>
              )}
            </div>

            <div className="form-field">
              <label>{form.engagement_type === 'recurring_referral' ? 'Client (referrer / paying party)' : 'Client'}</label>
              <select value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">Select a client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {isConsulting && (
              <div className="form-field">
                <label>Billing frequency <span className="form-hint">(for information)</span></label>
                <select value={form.billing_frequency || ''} onChange={e => set('billing_frequency', e.target.value)}>
                  <option value="">Not set</option>
                  {BILLING_FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
            {isConsulting && (
              <div className="form-field span-2">
                <label>Description / scope</label>
                <textarea rows={2} value={form.scope_description || ''} onChange={e => set('scope_description', e.target.value)} placeholder="e.g. Business consulting services" />
              </div>
            )}

            {!isConsulting && <div className="form-field">
              <label>Contractor</label>
              <select value={form.contractor_id} onChange={e => selectContractor(e.target.value)}>
                <option value="">Select a contractor…</option>
                {contractors.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({(c.contractor_subtype || 'corp_to_corp').replace('_', '-')})</option>)}
              </select>
            </div>}

            {form.engagement_type === 'contract_placement' && selectedContractor?.contractor_subtype === 'corp_to_corp' && (
              <div className="form-field span-2">
                <label>Vendor</label>
                <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
                  <option value="">Select a vendor…</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}
            {form.engagement_type === 'contract_placement' && selectedContractor && selectedContractor.contractor_subtype !== 'corp_to_corp' && (
              <div className="form-field span-2">
                <p className="form-hint">This contractor is {selectedContractor.contractor_subtype?.toUpperCase()} — paid directly by TMV, no vendor applies. The buy rate below is what TMV pays them directly.</p>
              </div>
            )}

            <div className="form-field">
              <label>Start date</label>
              <input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>End date</label>
              <input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
            </div>

            {form.engagement_type !== 'fte_placement' && !isConsulting && (
              <div className="form-field">
                <label>Sell rate ($/hr)</label>
                <input type="number" step="0.01" value={form.sell_rate || ''} onChange={e => set('sell_rate', e.target.value)} />
              </div>
            )}
            {form.engagement_type === 'contract_placement' && (
              <div className="form-field">
                <label>Buy rate ($/hr)</label>
                <input type="number" step="0.01" value={form.buy_rate || ''} onChange={e => set('buy_rate', e.target.value)} />
                <span className="form-hint">Only visible to admins — never shown to commission beneficiaries.</span>
              </div>
            )}
            {form.engagement_type === 'fte_placement' && (
              <div className="form-field span-2">
                <label>One-time placement fee</label>
                <input type="number" step="0.01" value={form.fte_fee_amount || ''} onChange={e => set('fte_fee_amount', e.target.value)} placeholder="e.g. 15000" />
                <span className="form-hint">The flat fee the client pays once this hire is finalized. You can generate the one-time invoice for this from the Work Order's Actions menu once it's set.</span>
              </div>
            )}
            {(form.engagement_type === 'fte_placement' || form.engagement_type === 'recurring_referral') && (
              <>
                <div className="form-field">
                  <label>Offer No <span className="form-hint">(optional)</span></label>
                  <input value={form.offer_number || ''} onChange={e => set('offer_number', e.target.value)} placeholder="e.g. O7399" />
                </div>
                <div className="form-field">
                  <label>Employee ID <span className="form-hint">(optional)</span></label>
                  <input value={form.employee_id || ''} onChange={e => set('employee_id', e.target.value)} placeholder="e.g. G4275" />
                </div>
                <div className="form-field span-2">
                  <span className="form-hint">Pre-fills the FTE/Referral fee invoice when generated. Not required — older placements may not have this on file.</span>
                </div>
              </>
            )}

            {!isEdit && (
              <>
                <div className="form-field span-2">
                  <label>Owner (Person)</label>
                  <select value={form.person_id} onChange={e => set('person_id', e.target.value)}>
                    <option value="">Select who owns this Work Order…</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.role ? ` (${p.role})` : ''}</option>)}
                  </select>
                  <span className="form-hint">Whoever sourced or manages this engagement — required for every Work Order, even when no commission applies.</span>
                </div>

                {!isConsulting && <div className="form-field">
                  <label>Commission (optional)</label>
                  <select value={form.commission_type} onChange={e => set('commission_type', e.target.value)}>
                    <option value="">No commission on this deal</option>
                    <option value="fixed">Fixed amount per period</option>
                    <option value="percentage">Percentage of revenue</option>
                  </select>
                </div>}
                {form.commission_type && (
                  <div className="form-field">
                    <label>{form.commission_type === 'fixed' ? 'Amount ($)' : 'Percentage (%)'}</label>
                    <input
                      type="number" step="0.01"
                      value={form.commission_value}
                      onChange={e => set('commission_value', e.target.value)}
                      placeholder={form.commission_type === 'fixed' ? 'e.g. 500' : 'e.g. 10'}
                    />
                  </div>
                )}
              </>
            )}

            <div className="form-field span-2">
              <label>Notes</label>
              <textarea rows={3} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          {isEdit && <DocumentWallet entityType="work_order" entityId={workOrder.id} entityName={workOrder.client_wo_number} />}
        </div>

        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create Work Order'}
          </button>
        </div>
      </div>
    </>
  );
}
